import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  formatDisplayDate,
  generateCalendarDays,
  handleDayClick,
} from 'utils/date'
import { CalendarMonth } from '@mui/icons-material'
import CalendarPopup from './components/calendar-popup/calendar-popup.component'
import { useEditMode } from '../../../contexts/edit-mode.context'
import {
  ComponentContainer,
  DateDisplay,
  DemoContainer,
  PickerWrapper,
  TextFieldInput,
} from './date-range-picker-field.styles'
import { InputLabel, Stack } from '@mui/material'

interface DateTimeRangeFieldProps {
  label: string
  name: string
  highlight?: boolean
  bold?: boolean
  disabled?: boolean
  hideIcon?: boolean
}

const DateTimeRangeField = ({
  label,
  name,
  highlight,
  bold,
  disabled,
  hideIcon,
}: DateTimeRangeFieldProps) => {
  const pickerRef = useRef<HTMLDivElement>(null)
  const { control, getValues, watch, setValue } = useFormContext()
  const { isEditMode, hasContext } = useEditMode()
  // Only apply edit mode logic if context exists, otherwise respect the disabled prop
  const isDisabled = disabled !== undefined 
    ? disabled 
    : (hasContext ? !isEditMode : false)
  const isViewMode = hasContext && !isEditMode
  const showHighlight = Boolean(highlight) && !isViewMode

  // Watch the form value for this field
  const fieldValue = watch(name)

  // State to manage if the calendar popup is open
  const [isOpen, setIsOpen] = useState(false)

  // Initialize range from form value
  const getInitialRange = (value: string | null | undefined): { start: Date | null; end: Date | null } => {
    if (!value) return { start: null, end: null }
    
    try {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return { start: date, end: date }
      }
    } catch (e) {
      // Invalid date
    }
    return { start: null, end: null }
  }

  // State for the selected date range, initialized from form value
  const [range, setRange] = useState<{ start: Date | null; end: Date | null }>(() => getInitialRange(fieldValue))

  // Update range when form value changes
  useEffect(() => {
    const newRange = getInitialRange(fieldValue)
    // Only update if the range actually changed to avoid unnecessary re-renders
    const startChanged = newRange.start?.getTime() !== range.start?.getTime()
    const endChanged = newRange.end?.getTime() !== range.end?.getTime()
    const startNullChanged = (newRange.start === null) !== (range.start === null)
    const endNullChanged = (newRange.end === null) !== (range.end === null)
    
    if (startChanged || endChanged || startNullChanged || endNullChanged) {
      setRange(newRange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue])

  // Helper to format date as YYYY-MM-DD in local timezone (to avoid timezone shift)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}T00:00:00`
  }

  // Update form value when range changes
  const handleRangeChange = (newRange: { start: Date | null; end: Date | null }) => {
    setRange(newRange)
    // Update form value with the start date (or end date if start is null)
    const dateToSet = newRange.start || newRange.end
    if (dateToSet) {
      // Use local date format to avoid timezone shift issues
      const formattedDate = formatDateLocal(dateToSet)
      setValue(name, formattedDate, { shouldValidate: true })
    } else {
      setValue(name, '', { shouldValidate: true })
    }
  }

  // State for the currently *displayed* month, based on the date value or current date
  const getInitialCurrentDate = useCallback((): Date => {
    if (fieldValue) {
      try {
        const date = new Date(fieldValue)
        if (!isNaN(date.getTime())) {
          return date
        }
      } catch (e) {
        // Invalid date
      }
    }
    // Fallback to first order event or current date
    const firstEventDate = getValues().orderEvents?.[0]?.startTime
    if (firstEventDate) {
      try {
        const date = new Date(firstEventDate)
        if (!isNaN(date.getTime())) {
          return date
        }
      } catch (e) {
        // Invalid date
      }
    }
    return new Date()
  }, [fieldValue, getValues])

  const [currentDate, setCurrentDate] = useState<Date>(getInitialCurrentDate)

  // Update current date when form value changes
  useEffect(() => {
    const newDate = getInitialCurrentDate()
    setCurrentDate(newDate)
  }, [fieldValue, getInitialCurrentDate])

  // Re-generate calendar days when the displayed month changes
  const calendarDays = useMemo(
    () => generateCalendarDays(currentDate),
    [currentDate]
  )

  // Get the month and year string for the header
  const monthYearDisplay = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  // --- Navigation Handlers ---
  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    )
  }

  const goToPrevMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    )
  }

  // Click-outside-to-close logic
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [pickerRef])

  return (
    <Controller
      name={name ?? ''}
      control={control}
      render={() => (
        <div data-testid="DateTimeRange.Field">
          <InputLabel
            sx={{
              color: showHighlight ? 'error.main' : 'secondary.dark',
              fontWeight: showHighlight || bold ? 700 : isViewMode ? 500 : 300,
              ...(isViewMode && {
                color: 'text.secondary',
                fontSize: '0.875rem',
              }),
            }}
          >
            <Stack>{label}</Stack>
          </InputLabel>
          <DemoContainer>
            <ComponentContainer>
              <PickerWrapper ref={pickerRef}>
                <TextFieldInput
                  $readOnly={isViewMode}
                  onClick={() => !isDisabled && setIsOpen(!isOpen)}
                  style={{
                    cursor: isDisabled ? 'default' : 'pointer',
                    opacity: isDisabled && !hasContext ? 0.6 : 1,
                  }}
                >
                  {!hideIcon && <CalendarMonth color="primary" />}
                  <DateDisplay $readOnly={isViewMode}>
                    {formatDisplayDate(range)}
                  </DateDisplay>
                </TextFieldInput>

                {isOpen && (
                  <CalendarPopup
                    range={range}
                    setRange={handleRangeChange}
                    onDayClick={handleDayClick}
                    calendarDays={calendarDays}
                    goToNextMonth={goToNextMonth}
                    goToPrevMonth={goToPrevMonth}
                    monthYearDisplay={monthYearDisplay}
                    setIsOpen={setIsOpen}
                  />
                )}
              </PickerWrapper>
            </ComponentContainer>
          </DemoContainer>
        </div>
      )}
    />
  )
}

export default DateTimeRangeField
